import { AppError, ERROR_CODES, type ErrorCode } from '@cravo/core'
import { AxiosError } from 'axios'

export function mapError(error: unknown): AppError {
  if (error instanceof AppError) return error

  if (error instanceof AxiosError) {
    if (error.code === 'ERR_CANCELED') {
      return new AppError('Request cancelled', ERROR_CODES.REQUEST_CANCELLED, 0)
    }

    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        return new AppError('Request timed out', ERROR_CODES.TIMEOUT, 0)
      }
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return new AppError('No internet connection', ERROR_CODES.OFFLINE, 0)
      }
      return new AppError('Unable to connect to the server', ERROR_CODES.NETWORK_ERROR, 0, undefined, error)
    }

    const { status, data } = error.response
    // Backend (Hono) returns either:
    //   { error: '<message>', code: '<CODE>' }  ← AppError shape
    //   { message: '<message>', code: '<CODE>' } ← legacy shape
    //   { error: <stringified ZodError fieldErrors> }
    // Read both keys to stay forward-compatible.
    const serverMessage =
      (typeof data?.error === 'string' && data.error) ||
      data?.message ||
      error.message ||
      'Something went wrong'
    const serverCode = data?.code as ErrorCode | undefined
    const fieldErrors = data?.errors
    const code = serverCode || httpStatusToCode(status)
    return new AppError(serverMessage, code, status, fieldErrors, data)
  }

  if (error instanceof Error) {
    return new AppError(error.message, ERROR_CODES.UNKNOWN, 0, undefined, error)
  }

  return new AppError('An unexpected error occurred', ERROR_CODES.UNKNOWN, 0, undefined, error)
}

function httpStatusToCode(status: number): ErrorCode {
  switch (status) {
    case 400: return ERROR_CODES.BAD_REQUEST
    case 401: return ERROR_CODES.UNAUTHORIZED
    case 403: return ERROR_CODES.FORBIDDEN
    case 404: return ERROR_CODES.NOT_FOUND
    case 409: return ERROR_CODES.EMAIL_ALREADY_EXISTS
    case 422: return ERROR_CODES.VALIDATION_ERROR
    case 429: return ERROR_CODES.RATE_LIMITED
    case 500: return ERROR_CODES.SERVER_ERROR
    case 502: case 503: case 504: return ERROR_CODES.SERVICE_UNAVAILABLE
    default: return ERROR_CODES.UNKNOWN
  }
}
